// CreateFab.jsx

// comment: Single file with 3 compact components:
// comment: - CreateFab (wrapper)
// comment: - CreateNodeForm (node creation; simpler & compact)
// comment: - CreateRelationForm (relationship creation; new UX)

import React, { useEffect, useMemo, useState } from "react";
import {
    Drawer,
    Fab,
    Tooltip,
    Box,
    Typography,
    TextField,
    Button,
    Divider,
    Stack,
    Tabs,
    Tab,
    IconButton,
    Alert,
    Autocomplete,
    Avatar,
    MenuItem,
    Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import LinkIcon from "@mui/icons-material/Link";

import { supabase } from "../../supabaseClient";
import {
    marginFab,
    separationFab,
    zIndexBotones,
    zIndexModals,
} from "./helpers";
import GroupChip from "./GroupChip";

/* ---------------------------------- helpers --------------------------------- */
// comment: preset pairs for quick opposites (extend freely)
const PRESET_PAIRS = [
    ["Padre", "Hijo"],
    ["Madre", "Hijo"],
    ["Abuelo", "Nieto"],
    ["Abuela", "Nieto"],
    ["Tío", "Sobrino"],
    ["Tía", "Sobrino"],
    ["Esposo", "Esposa"],
    ["Pareja", "Pareja"],
    ["Amigo", "Amigo"],
    ["Hermano", "Hermana"], // comment: when chosen, the opposite becomes "Hermano/Hermana" as same-side relation
    ["Hermana", "Hermano"],
];
// comment: build a bidirectional dictionary
const PRESET_MAP = PRESET_PAIRS.reduce((acc, [a, b]) => {
    acc[a] = b;
    acc[b] = a;
    return acc;
}, {});

const hexToRgb = (hex) => {
    const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
    if (!m) return { r: 0, g: 0, b: 0 };
    return {
        r: parseInt(m[1], 16),
        g: parseInt(m[2], 16),
        b: parseInt(m[3], 16),
    };
};
const getContrastText = (hex) => {
    const { r, g, b } = hexToRgb(hex);
    // comment: WCAG-ish luminance check; choose black or white
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq >= 128 ? "#000" : "#fff";
};

/* ---------------------------- Node creation form ---------------------------- */
const CreateNodeForm = ({ onCreated, grupos = [] }) => {
    // comment: existing state
    const [displayName, setDisplayName] = useState("");
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);
    const [session, setSession] = useState(null);

    const [selectedGrupos, setSelectedGrupos] = useState([]);

    useEffect(() => {
        let active = true;
        supabase.auth.getSession().then(({ data: { session } }) => {
            if (active) setSession(session);
        });
        const { data: { subscription } = {} } = supabase.auth.onAuthStateChange(
            (_, s) => setSession(s)
        );
        return () => subscription?.unsubscribe();
    }, []);

    const handleCreate = async () => {
        setError("");
        if (!session) {
            setError("Necesitas iniciar sesión.");
            return;
        }
        if (!displayName.trim()) {
            setError("Pon un nombre visible.");
            return;
        }
        try {
            setSaving(true);

            // comment: create person and return persona_id
            const { data: p, error: err } = await supabase
                .from("Persona")
                .insert([
                    {
                        display_name: displayName.trim(),
                        owner_id: session.user.id,
                    },
                ])
                .select("persona_id")
                .single();

            if (err) throw err;

            // comment: if there are selected groups, link them in GruposPersonas
            const grupoIds = selectedGrupos
                .map((g) => String(g.grupo_id))
                .filter(Boolean);
            if (p?.persona_id && grupoIds.length > 0) {
                const rows = grupoIds.map((gid) => ({
                    persona_id: String(p.persona_id),
                    grupo_id: gid,
                }));
                const { error: errGP } = await supabase
                    .from("GruposPersonas")
                    .insert(rows);
                if (errGP) throw errGP;
            }

            if (onCreated) await onCreated();

            // comment: reset fields for next creation
            setDisplayName("");
            setSelectedGrupos([]);
        } catch (e) {
            setError(e?.message || "No se pudo crear el nodo.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}

            <TextField
                label="Nombre visible"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                fullWidth
                autoFocus
            />

            {/* comment: new groups multi-select */}
            <Autocomplete
                multiple
                options={grupos}
                value={selectedGrupos}
                onChange={(_, v) => setSelectedGrupos(v)}
                getOptionLabel={(g) => g?.nombre || ""}
                // comment: emoji + name in the dropdown list as well
                renderOption={(props, option) => (
                    <li {...props} key={String(option.grupo_id)}>
                        <span style={{ marginRight: 8 }}>
                            {option.icon || ""}
                        </span>
                        <span style={{ flex: 1 }}>{option.nombre}</span>
                        <span
                            style={{
                                display: "inline-block",
                                width: 14,
                                height: 14,
                                borderRadius: 4,
                                background: option.color || "#ccc",
                                marginLeft: 8,
                                border: "1px solid rgba(0,0,0,0.2)",
                            }}
                        />
                    </li>
                )}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Grupos (0..n)"
                        placeholder="Selecciona grupos"
                    />
                )}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                        const bg = option.color || "#999999";
                        const fg = getContrastText(bg);
                        return (
                            <Chip
                                {...getTagProps({ index })}
                                key={String(option.grupo_id)}
                                label={`${option.icon || ""} ${
                                    option.nombre
                                }`.trim()}
                                sx={{
                                    bgcolor: bg,
                                    color: fg,
                                    border: "1px solid rgba(0,0,0,0.15)",
                                    "& .MuiChip-deleteIcon": {
                                        color: fg,
                                        opacity: 0.8,
                                    },
                                }}
                            />
                        );
                    })
                }
            />

            <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                    variant="contained"
                    onClick={handleCreate}
                    disabled={saving}
                >
                    Crear nodo
                </Button>
            </Stack>
        </Stack>
    );
};

/* ------------------------- Relationship creation form ----------------------- */
const CreateRelationForm = ({
    personas = [],
    relaciones = [],
    selectedPersonaId = "",
    onCreated,
    presetPersonaA,
    presetPersonaB,
}) => {
    // comment: priority for presets over selectedPersonaId
    const initialA = String(presetPersonaA || selectedPersonaId || "");
    const initialB = String(presetPersonaB || "");

    const [personaA, setPersonaA] = useState(initialA);
    const [personaB, setPersonaB] = useState(initialB);

    const [tipoAB, setTipoAB] = useState(""); // comment: A→B
    const [tipoBA, setTipoBA] = useState(""); // comment: B→A (independent unless reciprocal ON)
    const [reciprocal, setReciprocal] = useState(false);

    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);
    const [session, setSession] = useState(null);

    useEffect(() => {
        // comment: keep presets on open changes
        setPersonaA(initialA);
        setPersonaB(initialB);
    }, [initialA, initialB]);

    useEffect(() => {
        let active = true;
        supabase.auth.getSession().then(({ data: { session } }) => {
            if (active) setSession(session);
        });
        const { data: { subscription } = {} } = supabase.auth.onAuthStateChange(
            (_, s) => setSession(s)
        );
        return () => subscription?.unsubscribe();
    }, []);

    // comment: existing tipos for Autocomplete suggestions
    const existingTipos = useMemo(() => {
        const s = new Set(
            (relaciones || [])
                .map((r) => r?.tipo || "")
                .filter((t) => typeof t === "string" && t.trim() !== "")
                .map((t) => t.trim())
        );
        // comment: also include presets
        Object.keys(PRESET_MAP).forEach((k) => s.add(k));
        return Array.from(s).sort((a, b) => a.localeCompare(b));
    }, [relaciones]);

    // comment: if reciprocal is ON, lock tipoBA to tipoAB
    useEffect(() => {
        if (reciprocal) setTipoBA(tipoAB);
    }, [reciprocal, tipoAB]);

    // comment: whenever tipoAB changes and reciprocal is OFF, propose opposite into B←A if empty
    useEffect(() => {
        if (!reciprocal) {
            const opposite = PRESET_MAP[tipoAB?.trim()] || "";
            // comment: only auto-fill if nothing typed on BA or matches previous opposite
            if (!tipoBA || PRESET_MAP[tipoBA] === tipoAB) {
                setTipoBA(opposite);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tipoAB, reciprocal]);

    // comment: swap selected personas quickly
    const swapAB = () => {
        setPersonaA((a) => {
            const newA = personaB;
            setPersonaB(a);
            return newA;
        });
        // comment: swap direction labels too
        setTipoAB((t) => {
            const newAB = tipoBA;
            setTipoBA(t);
            return newAB;
        });
    };

    const validateCommon = () => {
        if (!session) return "Necesitas iniciar sesión.";
        if (!personaA || !personaB) return "Selecciona las dos personas.";
        if (String(personaA) === String(personaB))
            return "No puedes relacionar un nodo consigo mismo.";
        if (!tipoAB?.trim()) return "Indica el tipo para A→B.";
        return "";
    };

    const createRelations = async () => {
        setError("");
        const v = validateCommon();
        if (v) {
            setError(v);
            return;
        }
        try {
            setSaving(true);

            const rows = [];
            // comment: always create A→B
            rows.push({
                persona_a: String(personaA),
                persona_b: String(personaB),
                tipo: tipoAB.trim(),
                reciproca: Boolean(reciprocal),
            });

            // comment: if reciprocal ON, only one row with reciproca=true
            if (!reciprocal) {
                // comment: optional B→A if provided and not identical to A→B (allowed if same too, but usually different)
                if (tipoBA?.trim()) {
                    rows.push({
                        persona_a: String(personaB),
                        persona_b: String(personaA),
                        tipo: tipoBA.trim(),
                        reciproca: false,
                    });
                }
            }

            const { error: err } = await supabase
                .from("RelacionesPersonas")
                .insert(rows);
            if (err) throw err;

            if (onCreated) await onCreated();
            // comment: don't close automatically; reset types for quick next creation
            setTipoAB("");
            setTipoBA(reciprocal ? "" : "");
        } catch (e) {
            setError(e?.message || "No se pudo crear la relación.");
        } finally {
            setSaving(false);
        }
    };

    const personById = React.useMemo(
        () =>
            Object.fromEntries(personas.map((p) => [String(p.persona_id), p])),
        [personas]
    );
    console.log({ personById });

    return (
        <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}

            {/* comment: personas side-by-side with swap in the middle */}
            <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                    fullWidth
                    size="small"
                    label="Persona A"
                    value={personaA}
                    onChange={(e) => setPersonaA(e.target.value)}
                    select
                    SelectProps={{
                        // comment: show avatar + name when selected
                        renderValue: (val) => {
                            const p = personById[String(val)];
                            if (!p) return "Selecciona persona";
                            return (
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                    }}
                                >
                                    <Avatar
                                        alt={p.display_name}
                                        src={p.googlePicURL}
                                    />

                                    <span>{p.display_name}</span>
                                </Box>
                            );
                        },
                        // comment: ensure the menu opens above drawers/overlays if needed
                        MenuProps: { PaperProps: { sx: { maxHeight: 360 } } },
                    }}
                >
                    {personas.map((p) => (
                        <MenuItem
                            key={p.persona_id}
                            value={String(p.persona_id)}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                <Avatar
                                    alt={p.display_name}
                                    src={p.googlePicURL}
                                />

                                <span>{p.display_name}</span>
                            </Box>
                        </MenuItem>
                    ))}
                </TextField>

                <IconButton
                    aria-label="Intercambiar"
                    onClick={swapAB}
                    title="Intercambiar A↔B"
                >
                    {/* comment: use the ↔ symbol as requested */}
                    <SwapHorizIcon />
                </IconButton>

                <TextField
                    fullWidth
                    size="small"
                    label="Persona B"
                    value={personaB}
                    onChange={(e) => setPersonaB(e.target.value)}
                    select
                    SelectProps={{
                        renderValue: (val) => {
                            const p = personById[String(val)];
                            if (!p) return "Selecciona persona";
                            return (
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                    }}
                                >
                                    <Avatar
                                        alt={p.display_name}
                                        src={p.googlePicURL}
                                    />

                                    <span>{p.display_name}</span>
                                </Box>
                            );
                        },
                        MenuProps: { PaperProps: { sx: { maxHeight: 360 } } },
                    }}
                >
                    {personas.map((p) => (
                        <MenuItem
                            key={p.persona_id}
                            value={String(p.persona_id)}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                <Avatar
                                    alt={p.display_name}
                                    src={p.googlePicURL}
                                />
                                <span>{p.display_name}</span>
                            </Box>
                        </MenuItem>
                    ))}
                </TextField>
            </Stack>

            {/* comment: relation types row: A→B [↔ toggle] B→A */}
            <Stack direction="row" spacing={1} alignItems="center">
                <Autocomplete
                    fullWidth
                    options={existingTipos}
                    value={tipoAB || null}
                    onChange={(_, v) => setTipoAB(v || "")}
                    inputValue={tipoAB}
                    onInputChange={(_, v) => setTipoAB(v)}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="A → B"
                            placeholder="p.ej. Padre"
                        />
                    )}
                    freeSolo
                />

                <IconButton
                    aria-label="Recíproca"
                    color={reciprocal ? "primary" : "default"}
                    onClick={() => setReciprocal((x) => !x)}
                    title="Recíproca A↔B"
                >
                    {/* comment: middle symbol toggles reciprocal and mirrors values */}
                    <span style={{ fontSize: 18 }}>↔</span>
                </IconButton>

                <Autocomplete
                    fullWidth
                    options={existingTipos}
                    value={reciprocal ? tipoAB || null : tipoBA || null}
                    onChange={(_, v) => !reciprocal && setTipoBA(v || "")}
                    inputValue={reciprocal ? tipoAB : tipoBA}
                    onInputChange={(_, v) => !reciprocal && setTipoBA(v)}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="B → A"
                            placeholder={
                                reciprocal
                                    ? "Bloqueado por recíproca"
                                    : "p.ej. Hijo"
                            }
                            disabled={reciprocal}
                        />
                    )}
                    freeSolo
                />
            </Stack>

            {/* comment: small hint when an opposite is available */}
            {!!tipoAB?.trim() && !reciprocal && PRESET_MAP[tipoAB.trim()] && (
                <Typography variant="caption" color="text.secondary">
                    Sugerencia: “{tipoAB.trim()}” suele corresponder con “
                    {PRESET_MAP[tipoAB.trim()]}” en B→A.
                </Typography>
            )}

            <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                    variant="contained"
                    onClick={createRelations}
                    disabled={saving}
                    startIcon={<LinkIcon />}
                >
                    Crear relación
                </Button>
            </Stack>
        </Stack>
    );
};

/* ---------------------------- Group creation form --------------------------- */
const ExistingGroupsList = ({ grupos = [] }) => {
    // comment: show nothing if there are no groups
    if (!Array.isArray(grupos) || grupos.length === 0) return null;

    return (
        <Box sx={{ p: 2 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Grupos existentes
            </Typography>

            {/* comment: chips with each group's color and icon */}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {grupos.map((g) => {
                    const bg = g.color || "#999999";
                    const fg = getContrastText(bg); // comment: function already defined above
                    return (
                        <Chip
                            key={String(g.grupo_id)}
                            label={`${g.icon || ""} ${g.nombre}`.trim()}
                            sx={{
                                bgcolor: bg,
                                color: fg,
                                border: "1px solid rgba(0,0,0,0.15)",
                                mb: 1,
                            }}
                        />
                    );
                })}
            </Stack>
        </Box>
    );
};
const CreateGroupForm = ({ personas = [], onCreated, grupos }) => {
    // comment: create a group and optionally assign 0..n people
    const [nombre, setNombre] = useState("");
    const [icon, setIcon] = useState(""); // comment: emoji or short text
    const [color, setColor] = useState("#000000");
    const [selectedPersonas, setSelectedPersonas] = useState([]); // comment: array of full person objects
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);
    const [session, setSession] = useState(null);

    useEffect(() => {
        let active = true;
        supabase.auth.getSession().then(({ data: { session } }) => {
            if (active) setSession(session);
        });
        const { data: { subscription } = {} } = supabase.auth.onAuthStateChange(
            (_, s) => setSession(s)
        );
        return () => subscription?.unsubscribe();
    }, []);

    const handleCreateGroup = async () => {
        setError("");
        if (!session) {
            setError("Necesitas iniciar sesión.");
            return;
        }
        if (!nombre.trim()) {
            setError("Pon un nombre de grupo.");
            return;
        }
        try {
            setSaving(true);

            // comment: create group, return grupo_id
            const { data: g, error: errG } = await supabase
                .from("Grupos")
                .insert([
                    {
                        nombre: nombre.trim(),
                        color: color || "#000000",
                        icon: (icon || "").trim(),
                        owner_id: session.user.id,
                    },
                ])
                .select("grupo_id")
                .single();

            if (errG) throw errG;

            // comment: assign 0..n selected personas to GruposPersonas
            const personaIds = selectedPersonas
                .map((p) => String(p.persona_id))
                .filter(Boolean);

            if (g?.grupo_id && personaIds.length > 0) {
                const rows = personaIds.map((pid) => ({
                    persona_id: pid,
                    grupo_id: g.grupo_id,
                }));
                const { error: errGP } = await supabase
                    .from("GruposPersonas")
                    .insert(rows);
                if (errGP) throw errGP;
            }

            if (onCreated) await onCreated();

            // comment: reset for next creation
            setNombre("");
            setIcon("");
            setColor("#000000");
            setSelectedPersonas([]);
        } catch (e) {
            setError(e?.message || "No se pudo crear el grupo.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}

            <TextField
                label="Nombre del grupo"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                fullWidth
                autoFocus
            />

            <Stack direction="row" spacing={2}>
                <TextField
                    label="Icono (emoji opcional)"
                    value={icon}
                    onChange={(e) => setIcon(e.target.value)}
                    placeholder="p.ej. 👨‍👩‍👧‍👦"
                    fullWidth
                />
                <TextField
                    label="Color"
                    type="color"
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    // comment: small width to show the picker nicely
                    sx={{ width: 120 }}
                    InputLabelProps={{ shrink: true }}
                />
            </Stack>

            {/* comment: multi-select people, 0..n */}
            <Autocomplete
                multiple
                options={personas}
                value={selectedPersonas}
                onChange={(_, v) => setSelectedPersonas(v)}
                getOptionLabel={(p) => p?.display_name || ""}
                renderOption={(props, p) => (
                    <li {...props} key={String(p.persona_id)}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            <Avatar
                                alt={p.display_name}
                                src={p.googlePicURL || undefined}
                                imgProps={{ referrerPolicy: "no-referrer" }}
                                sx={{ width: 26, height: 26 }}
                            />
                            <span>{p.display_name}</span>
                        </Box>
                    </li>
                )}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Personas (0..n)"
                        placeholder="Selecciona personas"
                    />
                )}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                        <GroupChip
                            {...getTagProps({ index })} // comment: keeps remove-on-click behavior
                            key={String(option.grupo_id)}
                            nombre={option.nombre}
                            icon={option.icon} // comment: optional emoji/text
                            color={option.color || "#999999"} // comment: uses group color; fallback provided
                            deletable // comment: show the delete (x) icon like MUI Chip
                            size="small"
                            variant="filled"
                        />
                    ))
                }
            />

            <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                    variant="contained"
                    onClick={handleCreateGroup}
                    disabled={saving}
                    startIcon={<GroupAddIcon />}
                >
                    Crear grupo
                </Button>
            </Stack>
            <ExistingGroupsList grupos={grupos} />
        </Stack>
    );
};

/* ---------------------------------- wrapper --------------------------------- */
const TabPanel = ({ value, index, children }) => {
    if (value !== index) return null;
    return <Box sx={{ p: 2 }}>{children}</Box>;
};

const CreateFab = ({
    personas = [],
    relaciones = [],
    selectedPersonaId = "",
    onCreated,
    externalOpenKey,
    presetPersonaA = "",
    presetPersonaB = "",
    grupos = [],
    fabNumber = 0,
}) => {
    const [open, setOpen] = useState(false);
    const [tab, setTab] = useState(0);

    useEffect(() => {
        if (!externalOpenKey) return;
        // comment: open and jump to "Relación" when external key changes
        setOpen(true);
        setTab(1);
    }, [externalOpenKey]);

    return (
        <>
            <Tooltip title="Nuevo nodo o relación">
                <Fab
                    aria-label="create"
                    color="secondary"
                    onClick={() => setOpen(true)}
                    sx={{
                        position: "fixed",
                        top: marginFab + separationFab * fabNumber,
                        left: marginFab,
                        zIndex: zIndexBotones,
                    }}
                >
                    <AddIcon />
                </Fab>
            </Tooltip>

            <Drawer
                anchor="right"
                open={open}
                onClose={() => setOpen(false)}
                PaperProps={{
                    sx: {
                        width: 440,
                        maxWidth: "92vw",
                        display: "flex",
                        flexDirection: "column",
                        zIndex: zIndexModals,
                    },
                }}
            >
                <Box
                    sx={{ p: 2, display: "flex", alignItems: "center", gap: 1 }}
                >
                    <AddIcon />
                    <Typography variant="h6" sx={{ flex: 1 }}>
                        Crear
                    </Typography>
                    <Fab
                        size="small"
                        color="default"
                        onClick={() => setOpen(false)}
                        aria-label="close"
                    >
                        <CloseIcon />
                    </Fab>
                </Box>

                <Divider />

                <Tabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    variant="fullWidth"
                >
                    <Tab label="Persona" />
                    <Tab
                        icon={<LinkIcon fontSize="small" />}
                        iconPosition="start"
                        label="Relación"
                    />
                    <Tab
                        icon={<GroupAddIcon fontSize="small" />}
                        iconPosition="start"
                        label="Grupo"
                    />
                </Tabs>

                <TabPanel value={tab} index={0}>
                    <CreateNodeForm onCreated={onCreated} grupos={grupos} />
                    {/* comment: panel stays open after creation */}
                </TabPanel>

                <TabPanel value={tab} index={1}>
                    <CreateRelationForm
                        personas={personas}
                        relaciones={relaciones}
                        selectedPersonaId={selectedPersonaId}
                        onCreated={onCreated}
                        presetPersonaA={presetPersonaA}
                        presetPersonaB={presetPersonaB}
                    />
                    {/* comment: panel stays open after creation */}
                </TabPanel>

                <TabPanel value={tab} index={2}>
                    <CreateGroupForm
                        personas={personas}
                        onCreated={onCreated}
                        grupos={grupos}
                    />
                </TabPanel>
            </Drawer>
        </>
    );
};

export default CreateFab;
